import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRightIcon,
  UserCircleIcon,
  InboxIcon,
  PaperClipIcon
} from '@heroicons/react/24/solid';

// --- ASSUMED IMPORTS ---
// You will need to ensure these paths are correct in your project structure
import { useAuth } from '../../auth/firebaseAuth';
import { db } from '../../auth/firebaseConfig';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs
} from 'firebase/firestore';
import { getUserById } from '../../utility/firebaseHelpers';
import { formatRelativeTime } from '../../utility/formatters';
// --- END ASSUMED IMPORTS ---

interface Message {
  id: string;
  renterName: string;
  renterPhotoUrl?: string;
  propertyTitle: string;
  messageSnippet: string;
  timestamp: Date;
  // NEW: Crucial for displaying the unread badge/style
  isUnread: boolean;
}

const RecentMessagesPreview: React.FC = () => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRecentMessages = async () => {
      if (!currentUser) return;

      setIsLoading(true);
      setError(null);

      try {
        const conversationsRef = collection(db, 'conversations');
        // Optimized query for recent conversations involving the current user
        const q = query(
          conversationsRef,
          where('participants', 'array-contains', currentUser.uid),
          orderBy('lastMessageTimestamp', 'desc'),
          limit(5)
        );

        const querySnapshot = await getDocs(q);
        const messagesPromises = querySnapshot.docs.map(
          async (doc): Promise<Message | null> => {
            const data = doc.data();

            if (!data.participants || !Array.isArray(data.participants)) {
              return null;
            }

            const otherParticipantId = data.participants.find(
              (id: string) => id !== currentUser.uid
            );

            if (!otherParticipantId) {
              return null;
            }

            const otherUser = await getUserById(otherParticipantId);

            const messageSnippet = (() => {
              const lastMessage = data.lastMessage;
              if (typeof lastMessage === 'string') return lastMessage;
              if (lastMessage && typeof lastMessage === 'object') {
                return lastMessage.content || 'No message content';
              }
              return 'No message content';
            })();

            // UX IMPROVEMENT: Logic to determine unread status.
            const isUnread =
              data.currentUserUnread === true ||
              (data.lastMessageSenderId !== currentUser.uid &&
                data.unreadCount > 0);

            return {
              id: doc.id,
              renterName: otherUser?.name || 'Unknown User',
              renterPhotoUrl: otherUser?.photoUrl,
              propertyTitle: data.propertyTitle || 'Unnamed Property',
              messageSnippet,
              timestamp: data.lastMessageTimestamp?.toDate() || new Date(),
              isUnread: isUnread
            };
          }
        );

        const fetchedMessages = await Promise.all(messagesPromises);
        const validMessages = fetchedMessages.filter(
          (message): message is Message => message !== null
        );
        setMessages(validMessages);
      } catch (err) {
        console.error('Error fetching messages:', err);
        setError('Failed to load recent messages. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecentMessages();
  }, [currentUser]);

  // --- RENDERING LOGIC ---

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <div className="text-center text-gray-500">
          Loading recent messages...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <div className="text-center text-red-500">{error}</div>
      </div>
    );
  }

  return (
    // Updated container styling for a more robust look
    <div className="bg-white p-6 rounded-xl shadow-2xl border border-gray-100">
      <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center">
        <InboxIcon className="w-6 h-6 text-blue-600 mr-2" />
        Latest Inquiries
      </h2>
      <div className="space-y-3">
        {messages.length > 0 ? (
          <>
            {messages.map((message) => {
              // Enhanced logic for attachment check
              const isAttachment =
                message.messageSnippet.toLowerCase().includes('attachment') ||
                message.messageSnippet.toLowerCase().includes('file sent');
              const { isUnread } = message;

              // Dynamic classes including 'no-underline' for the main link block
              const linkClasses = `
                block group p-4 rounded-xl transition-all duration-200 no-underline
                ${
                  isUnread
                    ? 'bg-blue-50 border border-blue-200 hover:bg-blue-100'
                    : 'bg-white border border-gray-100 hover:bg-gray-50'
                }
              `;

              const nameClasses = `text-lg font-bold truncate no-underline
                ${isUnread ? 'text-gray-900' : 'text-gray-700'}`;

              return (
                <Link
                  key={message.id}
                  to={`/messages/${message.id}`}
                  className={linkClasses}
                >
                  <div className="flex items-start space-x-4">
                    {/* 1. Avatar with Unread Badge */}
                    <div className="flex-shrink-0 relative">
                      {message.renterPhotoUrl ? (
                        <img
                          src={message.renterPhotoUrl}
                          alt={message.renterName}
                          // Slightly smaller avatar for density
                          className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-sm"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center shadow-sm">
                          <UserCircleIcon className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      {/* Unread Indicator Badge (Dot) */}
                      {isUnread && (
                        <span className="absolute -top-1 -right-1 block h-3 w-3 rounded-full ring-2 ring-white bg-red-500" />
                      )}
                    </div>

                    {/* 2. Content Area */}
                    <div className="flex-1 min-w-0 pr-4">
                      {/* Top Row: Name and Time (Separated by flex-space) */}
                      <div className="flex items-baseline justify-between mb-1">
                        <h3 className={nameClasses}>{message.renterName}</h3>
                        {/* Time Utility and Conditional Styling */}
                        <p
                          className={`text-xs ml-3 whitespace-nowrap ${isUnread ? 'text-blue-600 font-semibold' : 'text-gray-500'}`}
                        >
                          {formatRelativeTime(message.timestamp)}
                        </p>
                      </div>

                      {/* Middle Row: Property Title (Muted) */}
                      <div className="mb-1">
                        {/* Note: This is a span, so no underline class is necessary here */}
                        <span className="text-xs font-medium text-gray-500 truncate inline-block max-w-[90%]">
                          {message.propertyTitle}
                        </span>
                      </div>

                      {/* Bottom Row: Message Preview (Line-clamped) */}
                      {isAttachment ? (
                        <div className="flex items-center space-x-1 text-sm text-gray-600">
                          <PaperClipIcon className="w-4 h-4 text-blue-400" />
                          <span
                            className={`${isUnread ? 'font-semibold text-blue-700' : 'text-gray-600'}`}
                          >
                            Attachment received
                          </span>
                        </div>
                      ) : (
                        // Conditional font weight for unread message snippet
                        <p
                          className={`text-sm line-clamp-1 ${isUnread ? 'text-gray-800 font-semibold' : 'text-gray-600'}`}
                        >
                          {message.messageSnippet}
                        </p>
                      )}
                    </div>

                    {/* 3. Arrow Indicator */}
                    <div className="flex-shrink-0 self-center">
                      <ChevronRightIcon className="w-5 h-5 text-gray-300 group-hover:text-blue-500 transition-colors" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </>
        ) : (
          // UX IMPROVEMENT: Clear and inviting Empty State
          <div className="p-8 text-center bg-gray-50 rounded-lg border border-dashed border-gray-200">
            <InboxIcon className="mx-auto w-10 h-10 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">
              No recent inquiries. If you've listed your property, tenant
              inquiries are just around the corner!
            </p>
            <Link
              to="/add-listing"
              // Added 'no-underline' here to remove the default link underline
              className="mt-4 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors no-underline hover:underline"
            >
              List a property now &rarr;
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentMessagesPreview;
